/**
 * Configuration Page - Step wizard for generating robot configs
 * When coming from Builder, reuses the same WebSocket connection and 3D viewer
 */

import { useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import {
  Box,
  Title,
  Text,
  Stepper,
  Button,
  Group,
  Paper,
  LoadingOverlay,
  Alert,
} from "@mantine/core";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { IconArrowLeft, IconAlertCircle } from "@tabler/icons-react";
import { UrdfInputStep } from "./steps/UrdfInputStep";
import { ServoMappingStep } from "./steps/ServoMappingStep";
import { MoveItStep } from "./steps/MoveItStep";
import { GazeboStep } from "./steps/GazeboStep";
import { ImitationStep } from "./steps/ImitationStep";
import { ExportStep } from "./steps/ExportStep";
import { useConfigureStore } from "./ConfigureStore";
import { toJointInfo, toLinkInfo } from "./urdfWire";
import { WebSocketProvider, useWebSocket } from "../../WebSocket";
import { Scene } from "../../scene";
import { usePageView } from "../../hooks/usePageView";

// Steps when coming from Builder (skip URDF input)
const STEPS_FROM_BUILDER = [
  { label: "Servo Mapping", description: "Configure joint-servo IDs" },
  { label: "MoveIt2", description: "Planning groups & controllers" },
  { label: "Gazebo", description: "Simulation settings" },
  { label: "Imitation Learning", description: "RL/IL config" },
  { label: "Export", description: "Download configs" },
];

// Steps when using standalone (need URDF input)
const STEPS_STANDALONE = [
  { label: "URDF Input", description: "Upload or paste URDF" },
  ...STEPS_FROM_BUILDER,
];

/**
 * Get WebSocket URL for the session
 */
function getWebSocketUrl(sessionId: string): string {
  const params = new URLSearchParams(window.location.search);
  const wsParam = params.get("ws");

  if (wsParam) {
    const separator = wsParam.includes("?") ? "&" : "?";
    return `${wsParam}${separator}session=${sessionId}`;
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const port = window.location.port || (protocol === "wss:" ? "443" : "80");
  return `${protocol}//${window.location.hostname}:${port}/session/${sessionId}`;
}

// Shared layout component
interface ConfigurePageLayoutProps {
  backUrl: string;
  backLabel: string;
  steps: Array<{ label: string; description: string }>;
  activeStep: number;
  setActiveStep: (step: number) => void;
  canProceed: (step: number) => boolean;
  nextStep: () => void;
  prevStep: () => void;
  renderStepContent: () => ReactNode;
  isLoading: boolean;
  loadError: string | null;
  onRetry?: () => void;
  onClearError?: () => void;
  showViewer: boolean;
}

function ConfigurePageLayout({
  backUrl,
  backLabel,
  steps,
  activeStep,
  setActiveStep,
  canProceed,
  nextStep,
  prevStep,
  renderStepContent,
  isLoading,
  loadError,
  onRetry,
  onClearError,
  showViewer,
}: ConfigurePageLayoutProps) {
  const navigate = useNavigate();

  return (
    <Box
      style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--mantine-color-gray-0)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        style={{
          height: 56,
          backgroundColor: "var(--mantine-color-body)",
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          display: "flex",
          alignItems: "center",
          padding: "0 16px",
        }}
      >
        <Group>
          <Button
            component={Link}
            to={backUrl}
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
          >
            {backLabel}
          </Button>
          <Title order={4}>Configuration Generator</Title>
        </Group>
      </Box>

      {/* Content - Two column layout */}
      <Box
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          padding: 16,
          gap: 16,
        }}
      >
        {/* Left: Configuration Steps */}
        <Paper
          shadow="sm"
          radius="md"
          p="xl"
          withBorder
          pos="relative"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
          }}
        >
          <LoadingOverlay visible={isLoading} zIndex={1000} />

          {/* Error Alert */}
          {loadError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Failed to load assembly"
              color="red"
              variant="light"
              mb="lg"
              withCloseButton
              onClose={onClearError}
            >
              {loadError}
              <Group mt="sm">
                {onRetry && (
                  <Button size="xs" onClick={onRetry}>
                    Retry
                  </Button>
                )}
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => navigate("/configure")}
                >
                  Use standalone mode
                </Button>
              </Group>
            </Alert>
          )}

          <Stepper
            active={activeStep}
            onStepClick={setActiveStep}
            allowNextStepsSelect={false}
            mb="xl"
          >
            {steps.map((step, index) => (
              <Stepper.Step
                key={index}
                label={step.label}
                description={step.description}
                allowStepSelect={index < activeStep || canProceed(index - 1)}
              />
            ))}
          </Stepper>

          {/* Step Content */}
          <Box mih={300} mb="xl">
            {renderStepContent()}
          </Box>

          {/* Navigation */}
          <Group justify="space-between">
            <Button
              variant="default"
              onClick={prevStep}
              disabled={activeStep === 0}
            >
              Back
            </Button>

            {activeStep < steps.length - 1 ? (
              <Button onClick={nextStep} disabled={!canProceed(activeStep)}>
                Next
              </Button>
            ) : (
              <Text size="sm" c="dimmed">
                Complete your export above
              </Text>
            )}
          </Group>
        </Paper>

        {/* Right: 3D Viewer */}
        <Paper
          shadow="sm"
          radius="md"
          withBorder
          style={{
            width: 400,
            minHeight: 400,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            p="sm"
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Text size="sm" fw={500}>
              Robot Preview
            </Text>
            <Text size="xs" c="dimmed">
              Hover on joints to highlight them
            </Text>
          </Box>
          <Box style={{ flex: 1, minHeight: 0 }}>
            {showViewer ? (
              <Scene />
            ) : (
              <Box
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <Text c="dimmed" size="sm">
                  Load a URDF to preview
                </Text>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

// Standalone mode component (no WebSocket)
function ConfigurePageStandalone() {
  const [activeStep, setActiveStep] = useState(0);

  const { urdfContent, joints, isUrdfParsed } = useConfigureStore();

  const steps = STEPS_STANDALONE;

  const canProceed = useCallback(
    (step: number): boolean => {
      switch (step) {
        case 0:
          return isUrdfParsed && urdfContent.length > 0;
        case 1:
          return joints.length > 0;
        case 2:
        case 3:
        case 4:
        case 5:
          return true;
        default:
          return false;
      }
    },
    [isUrdfParsed, urdfContent.length, joints.length]
  );

  const nextStep = useCallback(() => {
    if (activeStep < steps.length - 1 && canProceed(activeStep)) {
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, steps.length, canProceed]);

  const prevStep = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  }, [activeStep]);

  const renderStepContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <UrdfInputStep onNext={nextStep} />;
      case 1:
        return <ServoMappingStep />;
      case 2:
        return <MoveItStep />;
      case 3:
        return <GazeboStep />;
      case 4:
        return <ImitationStep />;
      case 5:
        return <ExportStep fromBuilder={false} />;
      default:
        return null;
    }
  }, [activeStep, nextStep]);

  return (
    <ConfigurePageLayout
      backUrl="/"
      backLabel="Home"
      steps={steps}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      canProceed={canProceed}
      nextStep={nextStep}
      prevStep={prevStep}
      renderStepContent={renderStepContent}
      isLoading={false}
      loadError={null}
      showViewer={false}
    />
  );
}

// Builder mode component (with WebSocket)
function ConfigurePageFromBuilder({ sessionId, packageName }: { sessionId: string; packageName: string }) {
  const wsContext = useWebSocket();
  const { isConnected: wsConnected } = wsContext;

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);

  const { joints, setUrdfContent, setUrdfParsed, setParseError, setPackageName, reset } =
    useConfigureStore();

  const steps = STEPS_FROM_BUILDER;

  // Reset store on mount and set package name from URL
  useEffect(() => {
    reset();
    setPackageName(packageName);
  }, [reset, setPackageName, packageName]);

  // Set up callback for receiving URDF data
  useEffect(() => {
    wsContext.setOnUrdfForConfigResult((result) => {
      if (result.success && result.urdf_content) {
        setUrdfContent(result.urdf_content, "assembly.urdf");
        setUrdfParsed(
          result.joints.map(toJointInfo),
          result.links.map(toLinkInfo),
          result.root_link,
        );
      } else {
        setLoadError(result.error || "Failed to get URDF");
        setParseError(result.error || "Failed to get URDF");
      }
      setIsLoading(false);
    });

    return () => {
      wsContext.setOnUrdfForConfigResult(null);
    };
  }, [wsContext, setUrdfContent, setUrdfParsed, setParseError]);

  // Send request when WebSocket is connected
  useEffect(() => {
    if (wsConnected && !requestSent) {
      console.log("WebSocket connected, sending get_urdf_for_config request");
      wsContext.sendGetUrdfForConfig();
      setRequestSent(true);
    }
  }, [wsConnected, requestSent, wsContext]);

  const retryLoad = useCallback(() => {
    setIsLoading(true);
    setLoadError(null);
    setRequestSent(false); // Will trigger the useEffect to send again
  }, []);

  const canProceed = useCallback(
    (step: number): boolean => {
      switch (step) {
        case 0:
          return joints.length > 0;
        case 1:
        case 2:
        case 3:
        case 4:
          return true;
        default:
          return false;
      }
    },
    [joints.length]
  );

  const nextStep = useCallback(() => {
    if (activeStep < steps.length - 1 && canProceed(activeStep)) {
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, steps.length, canProceed]);

  const prevStep = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  }, [activeStep]);

  // Memoize wsExport to avoid unnecessary re-renders
  const wsExport = useMemo(() => ({
    sendExportUrdf: wsContext.sendExportUrdf,
    setOnExportUrdfResult: wsContext.setOnExportUrdfResult,
  }), [wsContext.sendExportUrdf, wsContext.setOnExportUrdfResult]);

  const renderStepContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <ServoMappingStep />;
      case 1:
        return <MoveItStep />;
      case 2:
        return <GazeboStep />;
      case 3:
        return <ImitationStep />;
      case 4:
        return <ExportStep fromBuilder={true} wsExport={wsExport} />;
      default:
        return null;
    }
  }, [activeStep, wsExport]);

  return (
    <ConfigurePageLayout
      backUrl={`/builder/${sessionId}`}
      backLabel="Back to Builder"
      steps={steps}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      canProceed={canProceed}
      nextStep={nextStep}
      prevStep={prevStep}
      renderStepContent={renderStepContent}
      isLoading={isLoading}
      loadError={loadError}
      onRetry={retryLoad}
      onClearError={() => setLoadError(null)}
      showViewer={true}
    />
  );
}

// Main component that wraps with WebSocketProvider when coming from Builder
export function ConfigurePage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session");
  const packageName = searchParams.get("package") || "assembled_robot";

  usePageView(sessionId ?? undefined);

  // If coming from Builder, wrap with WebSocketProvider to connect to the same session
  if (sessionId) {
    const wsUrl = getWebSocketUrl(sessionId);

    // Store session ID for other components
    if (typeof window !== "undefined") {
      window.sessionId = sessionId;
    }

    return (
      <WebSocketProvider url={wsUrl}>
        <ConfigurePageFromBuilder sessionId={sessionId} packageName={packageName} />
      </WebSocketProvider>
    );
  }

  // Standalone mode - no WebSocket needed for initial load
  return <ConfigurePageStandalone />;
}
